import moment from "moment";

import { OutreachDraft, OutreachLead } from "../../../../types/OutreachMetrics";
import { StageModel, dueOf, isAutomatedStep } from "./leadUtils";

// One pass over the leads, one definition per concept.
//
// The screen used to compute "who needs a human", "no next action" and "in
// automation" in three different places with three different populations, so
// the tab badge, the lane tile and the queue heading could all print a
// different number for the same question. Everything on the screen now reads
// these fields, and the rule is simple: a COUNT is never filtered, only a
// visible list is. Filtering a count is what made "total active" change when a
// channel was picked.

export type QueueCounts = {
  // Leads a person owes a follow-up on, now. Overdue plus due today, never a
  // machine's wait, never a lead sitting on the reply-awaited stage.
  overdue: OutreachLead[];
  dueToday: OutreachLead[];
  // Everything above as one number. This is THE "needs a person" lead count.
  leadWork: number;
  // Active, human owned, nothing scheduled. Work to plan, not work due.
  noAction: number;
  // Active and owned by the automation. Not a person's work at all.
  inAutomation: number;
  // Active, human owned, due, but sitting on the reply-awaited stage. Waiting
  // on THEM. Counted so that leadWork + noAction + inAutomation + upcoming +
  // awaitingReply === activeCount, and the line under the queue can be honest.
  awaitingReply: number;
  // Active leads with a due date in the future.
  upcoming: number;
  activeCount: number;
  total: number;
  newToday: number;
};

export type DraftCounts = {
  // The one "drafts need a decision" number: pending, held or errored.
  needsDecision: number;
  pending: number;
  held: number;
  errors: number;
  approved: number;
};

export type FunnelModel = {
  steps: { label: string; count: number }[];
  won: number;
  lost: number;
  byChannel: {
    channel: string;
    total: number;
    won: number;
    active: number;
    replied: number;
  }[];
  contacted: number;
  replied: number;
  newThisWeek: number;
  contactedThisWeek: number;
};

export type QueueModel = QueueCounts & {
  drafts: DraftCounts;
  funnel: FunnelModel;
  channels: string[];
  leadCountByStage: Record<string, number>;
  lastUpdated?: string;
};

// The reply-awaited stage waits on THEM, not on us. Keeping it out of the work
// queue is what makes the queue count mean "things I owe".
const AWAITING_THEM = "reached";

export const buildDraftCounts = (drafts: OutreachDraft[]): DraftCounts => {
  const by = (status: string) =>
    drafts.filter((d) => d.status === status).length;
  const pending = by("pending");
  const held = by("held");
  const errors = by("error");
  return {
    pending,
    held,
    errors,
    approved: by("approved"),
    needsDecision: pending + held + errors,
  };
};

export const buildQueueModel = (
  leads: OutreachLead[],
  drafts: OutreachDraft[],
  model: StageModel,
  today: string
): QueueModel => {
  const wonSet = new Set(model.positiveTerminals);
  const lostSet = new Set(model.negativeTerminals);

  const overdue: OutreachLead[] = [];
  const dueToday: OutreachLead[] = [];
  let noAction = 0;
  let inAutomation = 0;
  let awaitingReply = 0;
  let upcoming = 0;
  let activeCount = 0;
  let newToday = 0;
  let won = 0;
  let lost = 0;

  const leadCountByStage: Record<string, number> = {};
  const channelSet = new Set<string>();
  let lastUpdated: string | undefined;

  const weekAgo = moment().subtract(7, "days");
  let newThisWeek = 0;
  let contactedThisWeek = 0;

  for (const lead of leads) {
    leadCountByStage[lead.stage] = (leadCountByStage[lead.stage] || 0) + 1;
    if (lead.channel) channelSet.add(lead.channel);
    if (lead.updatedAt && (!lastUpdated || lead.updatedAt > lastUpdated)) {
      lastUpdated = lead.updatedAt;
    }
    if ((lead.createdAt || "").slice(0, 10) === today) newToday += 1;
    if (lead.createdAt && moment(lead.createdAt).isAfter(weekAgo)) {
      newThisWeek += 1;
    }
    if (lead.lastContactedAt && moment(lead.lastContactedAt).isAfter(weekAgo)) {
      contactedThisWeek += 1;
    }
    if (wonSet.has(lead.stage)) won += 1;
    if (lostSet.has(lead.stage)) lost += 1;

    if (!model.isActive(lead.stage)) continue;
    activeCount += 1;

    if (isAutomatedStep(lead)) {
      inAutomation += 1;
      continue;
    }

    const due = dueOf(lead);
    if (!due) {
      noAction += 1;
      continue;
    }
    if (due > today) {
      upcoming += 1;
      continue;
    }
    if (lead.stage === AWAITING_THEM) {
      awaitingReply += 1;
      continue;
    }
    if (due < today) overdue.push(lead);
    else dueToday.push(lead);
  }

  const byDue = (a: OutreachLead, b: OutreachLead) =>
    (dueOf(a) || "").localeCompare(dueOf(b) || "");
  overdue.sort(byDue);
  dueToday.sort(byDue);

  const channels = Array.from(channelSet);

  // "Reached at least stage X" is the lead's funnel position at or past X.
  const reachedAtLeast = (lead: OutreachLead, key: string) => {
    const i = model.funnelIndex(lead.stage);
    return i >= 0 && i >= model.funnelIndex(key);
  };
  const steps = model.funnelStages.map((s) => ({
    label: s.label,
    count: leads.filter((l) => reachedAtLeast(l, s.key)).length,
  }));
  const repliedKey =
    model.funnelKeys.find((k) => k === "replied") || model.funnelKeys[2];
  const contactKey =
    model.funnelKeys.find((k) => k === AWAITING_THEM) || model.funnelKeys[1];

  const byChannel = channels.map((ch) => {
    const cl = leads.filter((l) => l.channel === ch);
    return {
      channel: ch,
      total: cl.length,
      won: cl.filter((l) => wonSet.has(l.stage)).length,
      active: cl.filter((l) => model.isActive(l.stage)).length,
      replied: repliedKey
        ? cl.filter((l) => reachedAtLeast(l, repliedKey)).length
        : 0,
    };
  });

  return {
    overdue,
    dueToday,
    leadWork: overdue.length + dueToday.length,
    noAction,
    inAutomation,
    awaitingReply,
    upcoming,
    activeCount,
    total: leads.length,
    newToday,
    drafts: buildDraftCounts(drafts),
    channels,
    leadCountByStage,
    lastUpdated,
    funnel: {
      steps,
      won,
      lost,
      byChannel,
      contacted: contactKey
        ? leads.filter((l) => reachedAtLeast(l, contactKey)).length
        : 0,
      replied: repliedKey
        ? leads.filter((l) => reachedAtLeast(l, repliedKey)).length
        : 0,
      newThisWeek,
      contactedThisWeek,
    },
  };
};

// The shape the worker throughput modal reads. Kept as its own projection so
// the modal keeps working without teaching it the whole queue model.
export const opsSummaryOf = (
  queue: QueueModel,
  lastRun: { at?: string | null; perChannel?: Record<string, unknown> } | null
) => {
  const sum = (k: string) =>
    Object.values(lastRun?.perChannel || {}).reduce(
      (n: number, c) => n + ((c as Record<string, number>)[k] || 0),
      0
    );
  return {
    newToday: queue.newToday,
    unworked: queue.noAction,
    draftsToReview: queue.drafts.pending + queue.drafts.held,
    draftErrors: queue.drafts.errors,
    approved: queue.drafts.approved,
    inFlight: queue.inAutomation,
    humanDue: queue.leadWork,
    ranAt: lastRun?.at || null,
    discoveredToday: sum("discovered"),
    draftedToday: sum("drafted"),
    shippedToday: sum("shipped"),
  };
};
